/**
 * Сторінка улюблених майстрів клієнта
 * @component FavoriteMastersPage
 * @file FavoriteMastersPage.tsx
 * @path frontend/src/pages/FavoriteMastersPage.tsx
 */

import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation } from '../../contexts/LocationContext';
import MastersTable from '../../components/MastersTable';
import WorkingHoursModal from '../../modals/WorkingHoursModal';
import {
  getClientFavorites,
  addClientFavorite,
  removeClientFavorite,
  getBusinessesByClientLocation,
  Business
} from '../../services/clientService';
import { Tooltip } from '@mui/material';
import { Star as StarIcon, Comment as CommentIcon, Favorite as FavoriteIcon, People as PeopleIcon, AccessTime as AccessTimeIcon } from '@mui/icons-material';
import { useMediaQuery } from '@mui/material';
import './style.css';

const FavoriteMastersPage: React.FC = () => {
  const { language } = useLanguage();
  const { isAuthenticated } = useAuth();
  const { currentCity } = useLocation();
  const [favoriteMasters, setFavoriteMasters] = useState<Business[]>([]);
  const [allMasters, setAllMasters] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'favorites' | 'all'>('favorites');
  const isMobile = useMediaQuery('(max-width:768px)');
  const [workingHoursModal, setWorkingHoursModal] = useState<{
    isOpen: boolean;
    masterId: string;
    masterName: string;
  }>({
    isOpen: false,
    masterId: '',
    masterName: '',
  });

  // Get token from localStorage
  const getToken = () => localStorage.getItem('auth_token');

  useEffect(() => {
    if (isAuthenticated) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [currentCity, isAuthenticated]);

  const loadData = async () => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Load favorites and all masters in parallel
      const [favorites, allBusinesses] = await Promise.all([
        getClientFavorites(token),
        getBusinessesByClientLocation(currentCity || undefined, token)
      ]);

      setFavoriteMasters(favorites);
      setAllMasters(allBusinesses);
    } catch (error) {
      console.error('Error loading masters:', error);
      // Set empty arrays on error
      setFavoriteMasters([]);
      setAllMasters([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFavorite = async (masterId: string) => {
    const token = getToken();
    if (!token) return;

    const success = await removeClientFavorite(masterId, token);
    if (success) {
      // Update local state
      setFavoriteMasters(prev => prev.filter(master => master.id !== masterId));
      // Update the all masters list to reflect the change
      setAllMasters(prev => prev.map(master =>
        master.id === masterId ? { ...master } : master
      ));
    }
  };

  const handleAddFavorite = async (masterId: string) => {
    const token = getToken();
    if (!token) return;

    const success = await addClientFavorite(masterId, token);
    if (success) {
      // Find the master in all masters and add to favorites
      const masterToAdd = allMasters.find(master => master.id === masterId);
      if (masterToAdd && !favoriteMasters.find(m => m.id === masterId)) {
        setFavoriteMasters(prev => [...prev, masterToAdd]);
        // Update the all masters list to reflect the change
        setAllMasters(prev => prev.map(master =>
          master.id === masterId ? { ...master } : master
        ));
      }
    }
  };

  const handleBookAppointment = (master: Business) => {
    // Open working hours modal for booking
    setWorkingHoursModal({
      isOpen: true,
      masterId: master.id,
      masterName: master.name,
    });
  };

  const handleShowWorkingHours = (master: Business) => {
    setWorkingHoursModal({
      isOpen: true,
      masterId: master.id,
      masterName: master.name,
    });
  };

  const handleCloseWorkingHours = () => {
    setWorkingHoursModal({
      isOpen: false,
      masterId: '',
      masterName: '',
    });
  };

  const currentMasters = activeTab === 'favorites' ? (favoriteMasters || []) : (allMasters || []);

  return (
    <div className="favorite-masters-page">
      <div className="page-header">
        <h1 className="page-title">
          {language === 'ua' ? 'Улюблені майстри' : 'Favorite Masters'}
        </h1>
        <p className="page-subtitle">
          {language === 'ua'
            ? 'Ваші улюблені майстри для швидкого бронювання'
            : 'Your favorite masters for quick booking'
          }
        </p>
      </div>

      <div className="page-content">
        {/* Tab Navigation */}
        <div className="tabs-navigation">
          <button
            className={`tab-button ${activeTab === 'favorites' ? 'active' : ''}`}
            onClick={() => setActiveTab('favorites')}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <FavoriteIcon sx={{ fontSize: '1.1rem' }} />
            <span 
              className="tab-text"
              style={{ 
                display: 'inline', 
                marginLeft: '0.5rem',
                whiteSpace: 'nowrap'
              }}
            >
              {isMobile 
                ? (language === 'ua' ? 'Мої' : 'My')
                : (language === 'ua' ? 'Мої майстри' : 'My Masters')
              } ({favoriteMasters?.length || 0})
            </span>
          </button>
          <button
            className={`tab-button ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <PeopleIcon sx={{ fontSize: '1.1rem' }} />
            <span 
              className="tab-text"
              style={{ 
                display: 'inline', 
                marginLeft: '0.5rem',
                whiteSpace: 'nowrap'
              }}
            >
              {isMobile 
                ? (language === 'ua' ? 'Всі' : 'All')
                : (language === 'ua' ? 'Всі майстри' : 'All Masters')
              } ({allMasters?.length || 0})
            </span>
          </button>
        </div>

        {loading ? (
          <div className="loading-state">
            <p>{language === 'ua' ? 'Завантаження...' : 'Loading...'}</p>
          </div>
        ) : currentMasters.length === 0 ? (
          <div className="empty-state">
            {activeTab === 'favorites' ? (
              <FavoriteIcon sx={{ fontSize: '3rem', color: '#ccc' }} />
            ) : (
              <PeopleIcon sx={{ fontSize: '3rem', color: '#ccc' }} />
            )}
            <h3>
              {activeTab === 'favorites'
                ? (language === 'ua' ? 'Немає улюблених майстрів' : 'No favorite masters yet')
                : (language === 'ua' ? 'Немає доступних майстрів' : 'No masters available')
              }
            </h3>
            <p>
              {activeTab === 'favorites'
                ? (language === 'ua'
                    ? 'Додайте майстрів до улюблених для швидкого доступу'
                    : 'Add masters to favorites for quick access'
                  )
                : (language === 'ua'
                    ? 'Наразі немає доступних майстрів'
                    : 'No masters are currently available'
                  )
              }
            </p>
          </div>
        ) : activeTab === 'favorites' ? (
          <div className="masters-grid">
            {currentMasters.map((master: Business) => (
              <div key={master.id} className="master-card favorite-highlight">
                <div className="master-avatar">
                  {master.logo_url ? (
                    <img src={master.logo_url} alt={master.name} />
                  ) : (
                    <div className="avatar-placeholder">
                      <i className="bi bi-person"></i>
                    </div>
                  )}
                  <button
                    className="favorite-btn active"
                    onClick={() => handleRemoveFavorite(master.id)}
                    title={language === 'ua' ? 'Видалити з улюблених' : 'Remove from favorites'}
                  >
                    <i className="bi bi-heart-fill"></i>
                  </button>
                </div>

                <div className="master-info">
                  <div className="master-header">
                    <h4>{master.name}</h4>
                    <button
                      className="working-hours-btn"
                      onClick={() => handleShowWorkingHours(master)}
                      title={language === 'ua' ? 'Переглянути години роботи' : 'View working hours'}
                    >
                      <AccessTimeIcon sx={{ fontSize: '1.1rem' }} />
                    </button>
                  </div>
                  {master.phone && (
                    <p className="master-phone">
                      <i className="bi bi-telephone"></i>{' '}
                      <a
                        href={`tel:${master.phone}`}
                        className="phone-link"
                        title={language === 'ua' ? 'Подзвонити' : 'Call'}
                      >
                        {master.phone}
                      </a>
                    </p>
                  )}
                  <p className="master-specialty">
                    {master.categories && master.categories.length > 0
                      ? master.categories.slice(0, 2).join(', ')
                      : (language === 'ua' ? 'Категорії не вказані' : 'No categories')
                    }
                  </p>
                  <div className="master-rating">
                    <StarIcon sx={{ color: '#ffd700', fontSize: '1rem' }} />
                    <span>{master.rating.toFixed(1)}</span>
                    {master.reviews_count !== undefined && master.reviews_count !== null && (
                      <Tooltip
                        title={
                          <div style={{
                            padding: '12px',
                            backgroundColor: 'white',
                            color: 'black',
                            borderRadius: '4px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            textAlign: 'center'
                          }}>
                            <div style={{ fontSize: '0.875rem', color: '#666' }}>
                              {language === 'ua'
                                ? 'Відгуки будуть доступні після імплементації API'
                                : 'Reviews will be available after API implementation'
                              }
                            </div>
                          </div>
                        }
                        arrow
                        placement="top"
                      >
                        <span className="reviews-count">
                          <CommentIcon
                            sx={{
                              fontSize: '1rem',
                              marginLeft: '4px',
                              marginRight: '2px',
                              color: '#007bff',
                              cursor: 'pointer',
                              '&:hover': { color: '#0056b3' }
                            }}
                          />
                          {master.reviews_count}
                        </span>
                      </Tooltip>
                    )}
                  </div>
                </div>

                <div className="master-actions">
                  <button
                    className="book-btn"
                    onClick={() => handleBookAppointment(master)}
                  >
                    {isMobile 
                      ? (language === 'ua' ? 'Запис' : 'Book')
                      : (language === 'ua' ? 'Записатися' : 'Book Now')
                    }
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : isMobile ? (
          <div className="masters-grid">
            {currentMasters.map((master: Business) => (
              <div key={master.id} className="master-card all-masters">
                <div className="master-avatar">
                  {master.logo_url ? (
                    <img src={master.logo_url} alt={master.name} />
                  ) : (
                    <div className="avatar-placeholder">
                      <i className="bi bi-person"></i>
                    </div>
                  )}
                  <button
                    className={`favorite-btn ${(favoriteMasters || []).some(fav => fav.id === master.id) ? 'active' : ''}`}
                    onClick={() => {
                      const isFav = (favoriteMasters || []).some(fav => fav.id === master.id);
                      if (isFav) {
                        handleRemoveFavorite(master.id);
                      } else {
                        handleAddFavorite(master.id);
                      }
                    }}
                    title={
                      (favoriteMasters || []).some(fav => fav.id === master.id)
                        ? (language === 'ua' ? 'Видалити з улюблених' : 'Remove from favorites')
                        : (language === 'ua' ? 'Додати до улюблених' : 'Add to favorites')
                    }
                  >
                    <i className={(favoriteMasters || []).some(fav => fav.id === master.id) ? 'bi bi-heart-fill' : 'bi bi-heart'}></i>
                  </button>
                </div>

                <div className="master-info">
                  <div className="master-header">
                    <h4>{master.name}</h4>
                    <button
                      className="working-hours-btn"
                      onClick={() => handleShowWorkingHours(master)}
                      title={language === 'ua' ? 'Переглянути години роботи' : 'View working hours'}
                    >
                      <AccessTimeIcon sx={{ fontSize: '1.1rem' }} />
                    </button>
                  </div>
                  {master.phone && (
                    <p className="master-phone">
                      <i className="bi bi-telephone"></i>{' '}
                      <a
                        href={`tel:${master.phone}`}
                        className="phone-link"
                        title={language === 'ua' ? 'Подзвонити' : 'Call'}
                      >
                        {master.phone}
                      </a>
                    </p>
                  )}
                  <p className="master-specialty">
                    {master.categories && master.categories.length > 0
                      ? master.categories.slice(0, 2).join(', ')
                      : (language === 'ua' ? 'Категорії не вказані' : 'No categories')
                    }
                  </p>
                  <div className="master-rating">
                    <StarIcon sx={{ color: '#ffd700', fontSize: '1rem' }} />
                    <span>{master.rating.toFixed(1)}</span>
                    {master.reviews_count !== undefined && master.reviews_count !== null && (
                      <Tooltip
                        title={
                          <div style={{
                            padding: '12px',
                            backgroundColor: 'white',
                            color: 'black',
                            borderRadius: '4px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            textAlign: 'center'
                          }}>
                            <div style={{ fontSize: '0.875rem', color: '#666' }}>
                              {language === 'ua'
                                ? 'Відгуки будуть доступні після імплементації API'
                                : 'Reviews will be available after API implementation'
                              }
                            </div>
                          </div>
                        }
                        arrow
                        placement="top"
                      >
                        <span className="reviews-count">
                          <CommentIcon
                            sx={{
                              fontSize: '1rem',
                              marginLeft: '4px',
                              marginRight: '2px',
                              color: '#007bff',
                              cursor: 'pointer',
                              '&:hover': { color: '#0056b3' }
                            }}
                          />
                          {master.reviews_count}
                        </span>
                      </Tooltip>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="masters-table-container">
            <MastersTable
              masters={currentMasters}
              favoriteMasters={favoriteMasters}
              onAddFavorite={handleAddFavorite}
              onRemoveFavorite={handleRemoveFavorite}
              onShowWorkingHours={handleShowWorkingHours}
              language={language}
            />
          </div>
        )}
      </div>

      {/* Working Hours Modal */}
      <WorkingHoursModal
        isOpen={workingHoursModal.isOpen}
        onClose={handleCloseWorkingHours}
        masterId={workingHoursModal.masterId}
        masterName={workingHoursModal.masterName}
      />
    </div>
  );
};

export default FavoriteMastersPage;
